<script setup>
import { ref, reactive, onMounted } from 'vue';

const props = defineProps({
  operation: Object
});

const emit = defineEmits(['save', 'close']);

const modal = ref(null);
const form = reactive({
  country: '',
  operator: '',
  title: '',
  ussdCode: '',
  instructions: [],
  requiredFields: []
});

const countries = {
  BJ: { name: 'Bénin', flag: '🇧🇯' },
  BF: { name: 'Burkina Faso', flag: '🇧🇫' },
  CI: { name: 'Côte d\'Ivoire', flag: '🇨🇮' },
  GA: { name: 'Gabon', flag: '🇬🇦' },
  ML: { name: 'Mali', flag: '🇲🇱' },
  NE: { name: 'Niger', flag: '🇳🇪' },
  SN: { name: 'Sénégal', flag: '🇸🇳' },
  TG: { name: 'Togo', flag: '🇹🇬' }
};

const operators = {
  MTN: { name: 'MTN' },
  MOOV: { name: 'Moov' },
  AIRTEL: { name: 'Airtel' },
  NITA: { name: 'Nita' }
};

onMounted(() => {
  if (props.operation) {
    open(props.operation);
  }
});

function addInstruction() {
  form.instructions.push({
    text: '',
    type: 'simple',
    action: '',
    actionContent: '',
    option1: null,
    option2: null
  });
}

function addDoubleOptionInstruction() {
  form.instructions.push({
    text: '',
    type: 'double',
    action: '',
    actionContent: '',
    option1: {
      text: '',
      actionContent: '',
      action: 'tel'
    },
    option2: {
      text: '',
      actionContent: '',
      action: 'copy'
    }
  });
}

function removeInstruction(index) {
  form.instructions.splice(index, 1);
}

function addRequiredField() {
  form.requiredFields.push({
    label: '',
    placeHolder: ''
  });
}

function removeRequiredField(index) {
  form.requiredFields.splice(index, 1);
}

function save() {
  if (!form.country || !form.operator) {
    alert('Veuillez remplir les champs obligatoires (pays, opérateur)');
    return;
  }

  const data = {
    country: form.country,
    operator: form.operator,
    title: 'Transférez montant FCFA',
    ussdCode: form.ussdCode,
    instructions: form.instructions.filter(i => i.text.trim()),
    requiredFields: form.requiredFields.filter(f => f.label.trim()).map(f => ({
    label: f.label,
    placeHolder: f.placeHolder
  }))
  };

  emit('save', data);
  close();
}

function close() {
  modal.value?.hide();
}

function onClosed() {
  emit('close');
}

function open(operation) {
  if (operation) {
    Object.assign(form, {
      country: operation.country || '',
      operator: operation.operator || '',
      ussdCode: operation.ussdCode || '',
      instructions: operation.instructions ? JSON.parse(JSON.stringify(operation.instructions)) : [],
      requiredFields: operation.requiredFields ? JSON.parse(JSON.stringify(operation.requiredFields)).map(f => ({
      label: f.label,
      placeHolder: f.placeHolder
    })) : []
  });
  } else {
    form.country = '';
    form.operator = '';
    form.ussdCode = '';
    form.instructions = [];
    form.requiredFields = [];
  }
  modal.value?.show();
}

defineExpose({ open });
</script>

<template>
  <DialogComponent ref="modal" type="modal" @closed="onClosed" mainClass="[&_.modalBox]:h-full">
    <div class="w-full h-full flex flex-col overflow-auto ">
      <!-- HEADER -->
      <div class="flex justify-between items-center p-6 border-b  sticky top-0 z-2">
        <h2 class="text-xl font-semibold text-gray-900">
          {{ operation ? 'Modifier' : 'Ajouter' }} une opération
        </h2>
      </div>

      <!-- CONTENT -->
      <div class="flex-1 overflow-auto py-6 px-2 space-y-6">
        <!-- BASIC INFO -->
        <div class="bg-slate-100 rounded-xl p-4">
          <h3 class="font-medium text-gray-900 mb-4">Informations de base</h3>
          <div class="grid grid-cols-2 gap-4">
            <!-- COUNTRY -->
            <div>
              <label class="block mb-2 text-sm font-medium text-gray-700">Pays *</label>
              <select v-model="form.country" class="inputComponent w-full">
                <option value="">Sélectionner...</option>
                <option v-for="(info, code) in countries" :key="code" :value="code">
                  {{ info.flag }} {{ info.name }}
                </option>
              </select>
            </div>

            <!-- OPERATOR -->
            <div>
              <label class="block mb-2 text-sm font-medium text-gray-700">Opérateur *</label>
              <select v-model="form.operator" class="inputComponent w-full">
                <option value="">Sélectionner...</option>
                <option v-for="(info, code) in operators" :key="code" :value="code">
                  {{ info.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- USSD CODE -->
          <div class="mt-4">
            <label class="block mb-2 text-sm font-medium text-gray-700">Code USSD</label>
            <input
              v-model="form.ussdCode"
              type="text"
              placeholder="Ex: *880*2*1*0000*montant#"
              class="inputComponent w-full"
            />
          </div>
        </div>

        <!-- INSTRUCTIONS -->
        <div class="bg-slate-100 rounded-xl p-4">
          <div class="flex justify-between items-center mb-4">
            <h3 class="font-medium text-gray-900">Instructions</h3>
            <div class="flex gap-2">
              <button
                class="px-3 py-1 text-xs bg-gray-100 rounded-lg hover:bg-gray-200"
                @click="addInstruction"
              >
                + Simple
              </button>
              <button
                class="px-3 py-1 text-xs bg-gray-100 rounded-lg hover:bg-gray-200"
                @click="addDoubleOptionInstruction"
              >
                + Double
              </button>
            </div>
          </div>

          <div v-if="form.instructions.length === 0" class="text-center py-8 text-gray-400 text-sm bg-gray-50 rounded-lg">
            Aucune instruction. Cliquez sur "+ Simple" ou "+ Double" pour ajouter.
          </div>

          <div v-else class="space-y-3">
            <div
              v-for="(instruction, index) in form.instructions"
              :key="index"
              class="bg-gray-50 rounded-lg p-4 border border-gray-200"
            >
              <div class="flex justify-between items-start mb-3">
                <span class="text-xs font-medium text-gray-500">Étape {{ index + 1 }}</span>
                <button
                  class="text-red-500 hover:text-red-700 text-sm"
                  @click="removeInstruction(index)"
                >
                  Supprimer
                </button>
              </div>

              <!-- INSTRUCTION TEXT -->
              <div class="mb-3">
                <label class="block mb-1 text-xs font-medium text-gray-600">Texte</label>
                <textarea
                  v-model="instruction.text"
                  rows="2"
                  placeholder="Etape 1 : Tapez le code USSD suivant"
                  class="inputComponent w-full text-sm"
                ></textarea>
              </div>

              <!-- SIMPLE INSTRUCTION -->
              <div v-if="!instruction.type || instruction.type === 'simple'" class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block mb-1 text-xs font-medium text-gray-600">Action</label>
                  <select v-model="instruction.action" class="inputComponent w-full text-sm">
                    <option value="">Aucune</option>
                    <option value="copy">Copier</option>
                    <option value="tel">Lancer USSD</option>
                  </select>
                </div>
                <div v-if="instruction.action">
                  <label class="block mb-1 text-xs font-medium text-gray-600">Contenu</label>
                  <input
                    v-model="instruction.actionContent"
                    type="text"
                    placeholder="*880#"
                    class="inputComponent w-full text-sm"
                  />
                </div>
              </div>

              <!-- DOUBLE OPTION INSTRUCTION -->
              <div v-else-if="instruction.type === 'double'" class="space-y-3">
                <div class="bg-slate-100 rounded-lg p-3 border">
                  <label class="block mb-2 text-xs font-medium text-gray-600">Option 1</label>
                  <div class="space-y-2">
                    <textarea
                      v-model="instruction.option1.text"
                      rows="2"
                      placeholder="Cliquez & Lancez *880#"
                      class="inputComponent w-full text-sm"
                    ></textarea>
                    <div class="grid grid-cols-2 gap-2">
                      <select v-model="instruction.option1.action" class="inputComponent text-sm">
                        <option value="tel">Lancer</option>
                        <option value="copy">Copier</option>
                      </select>
                      <input
                        v-model="instruction.option1.actionContent"
                        type="text"
                        placeholder="*880#"
                        class="inputComponent text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div class="bg-slate-100 rounded-lg p-3 border">
                  <label class="block mb-2 text-xs font-medium text-gray-600">Option 2</label>
                  <div class="space-y-2">
                    <textarea
                      v-model="instruction.option2.text"
                      rows="2"
                      placeholder="Cliquez & Copiez *880#"
                      class="inputComponent w-full text-sm"
                    ></textarea>
                    <div class="grid grid-cols-2 gap-2">
                      <select v-model="instruction.option2.action" class="inputComponent text-sm">
                        <option value="tel">Lancer</option>
                        <option value="copy">Copier</option>
                      </select>
                      <input
                        v-model="instruction.option2.actionContent"
                        type="text"
                        placeholder="*880#"
                        class="inputComponent text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- REQUIRED FIELDS -->
        <div class="bg-slate-100 rounded-xl p-4">
          <div class="flex justify-between items-center mb-4">
            <h3 class="font-medium text-gray-900">Champs requis</h3>
            <button
              class="px-3 py-1 text-xs bg-gray-100 rounded-lg hover:bg-gray-200"
              @click="addRequiredField"
            >
              + Ajouter un champ
            </button>
          </div>

          <div v-if="form.requiredFields.length === 0" class="text-center py-8 text-gray-400 text-sm bg-gray-50 rounded-lg">
            Aucun champ requis. Cliquez sur "+ Ajouter un champ" pour en ajouter.
          </div>

          <div v-else class="space-y-3">
            <div
              v-for="(field, index) in form.requiredFields"
              :key="index"
              class="bg-gray-50 rounded-lg p-3 border border-gray-200 flex gap-3 items-start"
            >
              <div class="flex-1 space-y-2">
                <input
                  v-model="field.label"
                  type="text"
                  placeholder="Label (ex: Référence de la transaction)"
                  class="inputComponent w-full text-sm"
                />
                <input
                  v-model="field.placeHolder"
                  type="text"
                  placeholder="Placeholder (ex: TX..........)"
                  class="inputComponent w-full text-sm"
                />
              </div>
              <button
                class="mt-2 text-red-500 hover:text-red-700"
                @click="removeRequiredField(index)"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- FOOTER -->
      <div class="flex justify-end gap-3 p-6 border-t bg-white sticky bottom-0">
        <button
          class="px-6 py-2 rounded-lg bg-gray-200 text-gray-700 font-medium hover:bg-gray-300"
          @click="close"
        >
          Annuler
        </button>
        <button
          class="px-6 py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700"
          @click="save"
        >
          Enregistrer
        </button>
      </div>
    </div>
  </DialogComponent>
</template>
